import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

/** One recognized line of text. */
interface OcrLine {
  text: string;
  mean?: number;
}

/** Everything the engine needs, served from our own origin. */
const ASSETS = '/assets/ocr/';
const MODELS = {
  detectionPath: `${ASSETS}ch_PP-OCRv4_det_infer.onnx`,
  recognitionPath: `${ASSETS}ch_PP-OCRv4_rec_infer.onnx`,
  dictionaryPath: `${ASSETS}ppocr_keys_v1.txt`,
};

/**
 * Pre-bundled by scripts/build-ocr-engine.mjs and loaded at runtime rather than
 * imported, so the engine stays out of Angular's build graph — see that script
 * for why it cannot go through the normal bundler.
 */
const ENGINE_URL = '/ocr/engine.mjs';

interface OcrEngineModule {
  Ocr: { create(options: { models: typeof MODELS }): Promise<{ detect(image: string): Promise<OcrLine[]> }> };
  ort: { env: { wasm: { wasmPaths: string; numThreads?: number } } };
}

/** Free the models (tens of MB of wasm heap) once the user has moved on. */
const IDLE_DISPOSE_MS = 5 * 60 * 1000;

/**
 * Client-side OCR for the VIN/plate camera scan, using PP-OCRv4 (PaddleOCR)
 * through ONNX Runtime Web.
 *
 * This replaced Tesseract, which could not read real plates or VIN labels at
 * all: measured on photos of an actual car it returned "50LUANU" for the plate
 * 58C0AMU and never resolved a VIN, because its models are trained on scanned
 * documents rather than photographs of stylised type. PP-OCRv4 is a scene-text
 * model and read the same three photos exactly, at 0.97-0.99 confidence, with
 * no cropping at all.
 *
 * The library is a dynamic import so its weight stays out of the initial bundle
 * and off the SSR path entirely.
 */
@Injectable({ providedIn: 'root' })
export class OcrService {
  private enginePromise: Promise<{ detect(image: string): Promise<OcrLine[]> }> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /**
   * Read every line of text out of a prepared canvas, most confident first.
   * Callers pick the value they want out of the lines (see scan-parsers).
   */
  async recognize(image: HTMLCanvasElement): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('OCR is only available in the browser');
    }
    this.cancelIdleTimer();
    try {
      const engine = await this.getEngine();
      const url = await canvasObjectUrl(image);
      try {
        const lines = await engine.detect(url);
        return lines.map(line => line.text).join('\n');
      } finally {
        URL.revokeObjectURL(url);
      }
    } finally {
      this.startIdleTimer();
    }
  }

  private getEngine(): Promise<{ detect(image: string): Promise<OcrLine[]> }> {
    if (!this.enginePromise) {
      this.enginePromise = this.createEngine().catch(error => {
        // Don't cache a failed load — let the next scan try again.
        this.enginePromise = null;
        throw error;
      });
    }
    return this.enginePromise;
  }

  private async createEngine(): Promise<{ detect(image: string): Promise<OcrLine[]> }> {
    // Held in a variable so the bundler treats this as a runtime URL rather than
    // a module to resolve and inline at build time.
    const url = ENGINE_URL;
    const { Ocr, ort } = (await import(/* @vite-ignore */ url)) as OcrEngineModule;

    // Point ONNX Runtime at our own copy of its wasm before it loads any;
    // left alone it reaches for a CDN. Threads need cross-origin isolation
    // headers we don't set, so keep it single-threaded and predictable.
    ort.env.wasm.wasmPaths = ASSETS;
    ort.env.wasm.numThreads = 1;

    return Ocr.create({ models: MODELS });
  }

  private startIdleTimer(): void {
    this.cancelIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.enginePromise = null;
    }, IDLE_DISPOSE_MS);
  }

  private cancelIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}

/** The engine loads images by URL, so hand it one pointing at our canvas. */
function canvasObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('Could not read the processed image'));
    }, 'image/png');
  });
}
